import React, { useCallback, useEffect, useState } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import {
	Alert,
	Box,
	Button,
	Chip,
	CircularProgress,
	Container,
	IconButton,
	Paper,
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableRow,
	TextField,
	Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import BlockIcon from "@mui/icons-material/Block";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";

const API = process.env.REACT_APP_LAMBDA_ARTEH_API_DOMAIN;

/**
 * Public API keys (Wave 4 M-04). Keys authenticate the read-only
 * /public/v1/* endpoints; the plaintext shows exactly once at creation.
 */
interface ApiKey {
	keyID: number;
	name: string;
	prefix: string;
	isActive: number;
	createdBy: string | null;
	createdAt: string;
	lastUsedAt: string | null;
}

const ApiKeys: React.FC = () => {
	const { getAccessTokenSilently } = useAuth0();
	const [keys, setKeys] = useState<ApiKey[]>([]);
	const [name, setName] = useState("");
	const [freshKey, setFreshKey] = useState<string | null>(null);
	const [loading, setLoading] = useState(true);
	const [saving, setSaving] = useState(false);
	const [error, setError] = useState<string | null>(null);

	const authFetch = useCallback(
		async (path: string, init?: RequestInit) => {
			const token = await getAccessTokenSilently();
			return fetch(`${API}${path}`, {
				...init,
				headers: {
					Authorization: `Bearer ${token}`,
					"Content-Type": "application/json",
					...(init?.headers || {}),
				},
			});
		},
		[getAccessTokenSilently],
	);

	const load = useCallback(async () => {
		setLoading(true);
		try {
			const res = await authFetch("/api/api-keys");
			const json = await res.json().catch(() => ({}));
			if (!res.ok || json.Ok === false)
				throw new Error(json.Message || "Could not load API keys");
			setKeys((json.Data || json).keys || []);
		} catch (e: any) {
			setError(e.message || "Could not load API keys");
		} finally {
			setLoading(false);
		}
	}, [authFetch]);

	useEffect(() => {
		load();
	}, [load]);

	const create = async () => {
		setSaving(true);
		setError(null);
		try {
			const res = await authFetch("/api/api-keys", {
				method: "POST",
				body: JSON.stringify({ name }),
			});
			const json = await res.json().catch(() => ({}));
			if (!res.ok || json.Ok === false)
				throw new Error(json.Message || "Could not create the key");
			setFreshKey((json.Data || json).key);
			setName("");
			await load();
		} catch (e: any) {
			setError(e.message || "Could not create the key");
		} finally {
			setSaving(false);
		}
	};

	const revoke = async (id: number) => {
		await authFetch(`/api/api-keys/${id}`, { method: "DELETE" });
		await load();
	};

	return (
		<Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
			<Paper elevation={2} sx={{ p: 4 }}>
				<Typography variant="h5">API keys</Typography>
				<Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
					Keys authenticate the read-only public API. Send the key in
					an <code>X-Api-Key</code> header to{" "}
					<code>GET {API}/public/v1/emissions?year=YYYY</code> (also{" "}
					<code>/categories</code> and <code>/data-quality</code>).
				</Typography>

				{error && (
					<Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>
				)}
				{freshKey && (
					<Alert
						severity="success"
						sx={{ mb: 2 }}
						onClose={() => setFreshKey(null)}
						action={
							<IconButton
								size="small"
								onClick={() => navigator.clipboard?.writeText(freshKey)}
								aria-label="copy"
							>
								<ContentCopyIcon fontSize="small" />
							</IconButton>
						}
					>
						Copy this key now — it is not shown again:{" "}
						<code>{freshKey}</code>
					</Alert>
				)}

				<Box sx={{ display: "flex", gap: 2, mb: 3 }}>
					<TextField
						size="small"
						label="Key name (what will use it)"
						value={name}
						onChange={(e) => setName(e.target.value)}
						sx={{ flex: 1 }}
					/>
					<Button
						variant="contained"
						startIcon={<AddIcon />}
						onClick={create}
						disabled={saving || !name.trim()}
					>
						Create key
					</Button>
				</Box>

				{loading ? (
					<Box sx={{ textAlign: "center", py: 4 }}><CircularProgress /></Box>
				) : keys.length === 0 ? (
					<Typography color="text.secondary" sx={{ textAlign: "center", py: 3 }}>
						No API keys yet.
					</Typography>
				) : (
					<Table size="small">
						<TableHead>
							<TableRow>
								<TableCell>Name</TableCell>
								<TableCell>Key</TableCell>
								<TableCell>Status</TableCell>
								<TableCell>Last used</TableCell>
								<TableCell />
							</TableRow>
						</TableHead>
						<TableBody>
							{keys.map((k) => (
								<TableRow key={k.keyID}>
									<TableCell>{k.name}</TableCell>
									<TableCell><code>{k.prefix}</code></TableCell>
									<TableCell>
										<Chip
											size="small"
											color={k.isActive ? "success" : "default"}
											label={k.isActive ? "Active" : "Revoked"}
										/>
									</TableCell>
									<TableCell>
										{k.lastUsedAt
											? new Date(k.lastUsedAt).toLocaleString()
											: "Never"}
									</TableCell>
									<TableCell align="right">
										{!!k.isActive && (
											<IconButton size="small" onClick={() => revoke(k.keyID)} aria-label="revoke">
												<BlockIcon fontSize="small" />
											</IconButton>
										)}
									</TableCell>
								</TableRow>
							))}
						</TableBody>
					</Table>
				)}
			</Paper>
		</Container>
	);
};

export default ApiKeys;
